import { differenceInMinutes } from 'date-fns';

import { RbiOrderStatus, useGetUserOrdersQuery } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';

import { CLOSER_STORE_RECENT_ORDER_COOLDOWN_MINUTES } from '../utils';

/**
 * Suppresses the closer-store prompt for a short window after the guest's most recent
 * order, so it does not interrupt an order still being placed or picked up.
 *
 * Deliberately independent of `useIsOrderInProgress`/`useHasOrderNotification`, which key
 * off `TOP_ORDER_STATUS_BAR_TIME_OUT` — a flag meant to control how long the unrelated
 * order-status banner stays visible (safe default 120 minutes), not whether a different
 * feature should still be gated. Reusing it here previously meant ANY recent order —
 * including a pickup order the guest had already collected, since pickup has no
 * fulfillment-completion signal to check against, unlike delivery — silently suppressed
 * this prompt for up to that same, much longer window.
 */
export const useRecentOrderCooldown = (): boolean => {
  const { isAuthenticated } = useAuthContext();

  const { data } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [RbiOrderStatus.INSERT_SUCCESSFUL, RbiOrderStatus.UPDATE_SUCCESSFUL],
    },
    skip: !isAuthenticated,
  });

  const recentOrder = data?.userOrders?.orders?.[0];
  if (!recentOrder?.updatedAt) {
    return false;
  }

  return (
    Math.abs(differenceInMinutes(new Date(), new Date(recentOrder.updatedAt))) <
    CLOSER_STORE_RECENT_ORDER_COOLDOWN_MINUTES
  );
};
